import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompliance } from '../../contexts/ComplianceContext';
import { ShieldAlert, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import securityApi from '../../api/securityApi';

const RiskAssessmentStep = () => {
  const navigate = useNavigate();
  const { projectId, resetWizard } = useCompliance();
  const [status, setStatus] = useState('idle'); // idle, generating, complete, error
  const [risks, setRisks] = useState([]);

  // Auto-trigger generation on mount
  useEffect(() => {
    if (!projectId) return;

    const generate = async () => {
      // Check if already exists
      const existing = await securityApi.getRiskAssessment(projectId);
      if (existing && existing.risks?.length > 0) {
        setRisks(existing.risks);
        setStatus('complete');
        return;
      }

      setStatus('generating');
      try {
        await securityApi.generateRiskAssessment(projectId);
      } catch (err) {
        if (err.response?.status === 400) {
          // Already exists but maybe still generating — poll for it
        } else {
          setStatus('error');
          return;
        }
      }

      // Poll until risks are available
      const poll = setInterval(async () => {
        try {
          const data = await securityApi.getRiskAssessment(projectId);
          if (data && data.risks?.length > 0) {
            setRisks(data.risks);
            setStatus('complete');
            clearInterval(poll);
          }
        } catch {
          // Keep polling
        }
      }, 3000);

      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(poll);
        if (status === 'generating') {
          setStatus('complete');
        }
      }, 120000);

      return () => clearInterval(poll);
    };

    generate();
  }, [projectId]);

  const handleFinish = () => {
    toast.success('Compliance project created successfully!');
    resetWizard();
    navigate(`/compliance/projects/${projectId}`);
  };

  const handleViewRiskAssessment = () => {
    resetWizard();
    navigate(`/compliance/projects/${projectId}/risk-assessment`);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-8">
      {status === 'idle' || status === 'generating' ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
            <Loader className="w-10 h-10 text-teal-600 animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Generating Your Risk Assessment
          </h2>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            We're analyzing your company's infrastructure, integrations, and compliance posture
            to generate relevant security risks.
          </p>
          <p className="text-sm text-gray-400">This may take 30-60 seconds...</p>
        </div>
      ) : status === 'error' ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Risk Generation Failed
          </h2>
          <p className="text-gray-500 mb-8">
            Don't worry — you can generate risks later from the Risk Assessment page.
          </p>
          <button
            onClick={handleFinish}
            className="px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            Continue to Project
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Risk Assessment Generated
            </h2>
            <p className="text-gray-500 mt-1">
              {risks.length} risks identified based on your company's profile
            </p>
          </div>

          {/* Preview table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider grid grid-cols-4 gap-2">
              <div className="col-span-2">Risk</div>
              <div>Likelihood</div>
              <div>Impact</div>
            </div>
            {risks.slice(0, 8).map((risk) => (
              <div
                key={risk.id}
                className="px-4 py-2.5 border-t border-gray-100 grid grid-cols-4 gap-2 text-sm"
              >
                <div className="col-span-2 text-gray-900 truncate">{risk.title}</div>
                <div className="text-gray-600 capitalize">{risk.likelihood?.replace('_', ' ')}</div>
                <div className="text-gray-600 capitalize">{risk.impact}</div>
              </div>
            ))}
            {risks.length > 8 && (
              <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 text-center">
                +{risks.length - 8} more risks
              </div>
            )}
          </div>

          <p className="text-sm text-gray-500 text-center">
            You can customize these risks later — modifying descriptions, adjusting ratings,
            and adding your own risks before approving.
          </p>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleViewRiskAssessment}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Customize Risks Now
            </button>
            <button
              onClick={handleFinish}
              className="px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
            >
              Finish Setup
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentStep;
